import type { Sound } from "./audio.ts"
import { KINDS } from "./break.ts"
import type { Hand } from "./groove.ts"
import type { EventKind } from "./world.ts"
import { HAT, KICK, SNARE, SNARE_COUNTS, TOM } from "./world.ts"

/**
 * What a Step sounds: the page maps a Cell's sound and the Step's count to one
 * of the core's sound codes, and the core knows nothing of Grids or counts.
 * Pure functions only.
 */

/** The sound codes by kind name, in `KINDS` order, so the page and the core share one numbering. */
export const SOUND = Object.fromEntries(KINDS.map((kind, i) => [kind, i + 1])) as Readonly<
  Record<(typeof KINDS)[number], Sound>
>

const OF_CELL: Readonly<Record<EventKind, Sound>> = {
  0: 0,
  [KICK]: SOUND.kick,
  [SNARE]: SOUND.snare,
  [HAT]: SOUND.hat,
  [TOM]: SOUND.tom
}

/** A snare off beats 2 and 4 is a ghost: still heard, never a mistake. Everything else is itself. */
export const soundFor = (event: EventKind, count: number): Sound =>
  event === SNARE && !SNARE_COUNTS.includes(count) ? SOUND.ghost : OF_CELL[event]

/** Which hand plays a sound, for its Drift: a ghost is the snare's hand, and a rest has none. */
export const handOf = (sound: Sound): Hand | undefined => {
  const kind = KINDS[sound - 1]
  return kind === "ghost" ? "snare" : kind
}

/**
 * How hard a sound is struck. A full snare is about -2.5 dB: hit for hit it is
 * no louder than a kick, but it is the commonest Event, rings twice as long and
 * cracks where the ear is keenest, so it stood out of the mix. Tune by ear.
 */
export const velocityFor = (sound: Sound): number => (sound === SOUND.snare ? 0.75 : 1)
